import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Alert, Button, Card, Input, Select, Spin, Typography } from 'antd';
import { useAuth } from '@clerk/clerk-react';
import { COMPETENCIES, CompetencySlug, ConversationRole, QuestionType, StreamEventType, AssessmentResponseSchema } from '@evaltick/shared';
import type { AssessmentResponse } from '@evaltick/shared';
import { AgentService, QuestionsService } from '@/services/api';
import type { Question } from '@/services/api';
import { AssessmentSummary } from '@/components';
import { DimensionBreakdown } from '@/components/DimensionBreakdown';
import { CoachPanel } from '@/components/Interview/CoachPanel';
import type { ConversationEntry } from '@/store/interviewStore';
import { streamText, calcStrength, containerVariants, itemVariants } from '@/utils';
import styles from './Evaluate.module.scss';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const COMPETENCY_OPTIONS = COMPETENCIES.map((c) => ({ label: c.name, value: c.id }));

const QUESTION_TYPE_OPTIONS = [
  { label: 'Behavioural', value: QuestionType.BEHAVIOURAL },
  { label: 'Situational', value: QuestionType.SITUATIONAL },
];

type Phase = 'idle' | 'streaming' | 'fetching' | 'done' | 'error';

// Persist the most recent assessment so it survives a page reload.
const STORED_RESULT_KEY = 'evaltick_last_assessment';

const loadStoredResult = (): AssessmentResponse | null => {
  try {
    const raw = localStorage.getItem(STORED_RESULT_KEY);
    if (!raw) return null;
    const parsed = AssessmentResponseSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const persistResult = (result: AssessmentResponse | null): void => {
  try {
    if (result) {
      localStorage.setItem(STORED_RESULT_KEY, JSON.stringify(result));
    } else {
      localStorage.removeItem(STORED_RESULT_KEY);
    }
  } catch {
    // Ignore storage failures (private mode, quota) — persistence is best-effort.
  }
};

export const Evaluate: React.FC = () => {
  const { getToken } = useAuth();

  const [competencyId, setCompetencyId] = useState<CompetencySlug>(CompetencySlug.DecisionMaking);
  const [questionType, setQuestionType] = useState<typeof QuestionType[keyof typeof QuestionType]>(QuestionType.BEHAVIOURAL);
  const [answer, setAnswer] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AssessmentResponse | null>(() => loadStoredResult());
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);

  // Coach conversation for the current result — seeded from a reloaded stored
  // result (as an already-finalized message) so follow-up chat works right
  // away without replaying the reveal animation.
  const [coachMessages, setCoachMessages] = useState<ConversationEntry[]>(() => {
    const stored = loadStoredResult();
    return stored?.rationale
      ? [{ role: ConversationRole.Coach, content: stored.rationale, isStreaming: false }]
      : [];
  });
  const [coachStreaming, setCoachStreaming] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  // Teardown handles for the reveal timers / follow-up streams kicked off by
  // handleSubmit / handleFollowUp, so they stop mutating state after unmount.
  const cancellersRef = useRef<Array<() => void>>([]);
  const registerCanceller = useCallback((cancel: () => void) => {
    cancellersRef.current.push(cancel);
  }, []);

  // Stop any in-flight reveal / follow-up stream. Called before starting a new
  // reveal (and on unmount) so a superseded reveal can't keep ticking chunks
  // into — or prematurely finalize — the next coach message.
  const cancelPendingReveals = useCallback(() => {
    cancellersRef.current.forEach((cancel) => cancel());
    cancellersRef.current = [];
  }, []);

  // Abort any in-flight evaluation when the component unmounts so the stream is
  // cancelled and no state updates fire after teardown.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => () => cancelPendingReveals(), [cancelPendingReveals]);

  const appendCoachChunk = useCallback((chunk: string) => {
    setCoachMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last?.isStreaming) return prev;
      return [...prev.slice(0, -1), { ...last, content: last.content + chunk }];
    });
  }, []);

  const finalizeCoachMessage = useCallback(() => {
    setCoachMessages((prev) => prev.map((m) => ({ ...m, isStreaming: false })));
    setCoachStreaming(false);
  }, []);

  const addUserCoachMessage = useCallback((content: string) => {
    setCoachMessages((prev) => [...prev, { role: ConversationRole.Candidate, content, isStreaming: false }]);
  }, []);

  // Appends the warning text before finalizing — finalizing first would mark
  // the message as no-longer-streaming, and appendCoachChunk only writes to
  // messages that are still streaming, so the error text would be dropped.
  const showCoachError = useCallback(
    (message: string) => {
      appendCoachChunk(`\n\n⚠️ ${message}`);
      finalizeCoachMessage();
    },
    [appendCoachChunk, finalizeCoachMessage]
  );

  // Questions now live in the Question Bank (database), not in the static
  // competency definitions — load every question for the selected (competency,
  // type) pair so the admin can pick the specific one to evaluate against.
  const fetchQuestions = useCallback(async (): Promise<Question[]> => {
    const token = await getToken();
    if (!token) return [];
    return new QuestionsService(token).list({ competencyId, questionType });
  }, [getToken, competencyId, questionType]);

  // Reload the question list whenever the competency/type selection changes,
  // defaulting the selection to the first available question.
  useEffect(() => {
    let cancelled = false;
    setQuestionsLoading(true);
    setQuestions([]);
    setSelectedQuestionId(null);
    fetchQuestions()
      .then((list) => {
        if (cancelled) return;
        setQuestions(list);
        setSelectedQuestionId(list[0]?.id ?? null);
      })
      .catch((err) => {
        // Best-effort — the question is optional context for the evaluation, so
        // a failed load leaves an empty picker rather than blocking the page.
        console.warn('Failed to load questions', err);
      })
      .finally(() => { if (!cancelled) setQuestionsLoading(false); });
    return () => { cancelled = true; };
  }, [fetchQuestions]);

  const questionText = questions.find((q) => q.id === selectedQuestionId)?.text ?? '';

  // Keep displayed result and persisted copy in sync.
  const updateResult = (next: AssessmentResponse | null) => {
    setResult(next);
    persistResult(next);
  };

  // Clears the score + coach conversation together — used whenever the
  // competency/question-type/question selection changes mid-flow.
  const resetResultAndCoach = () => {
    abortRef.current?.abort();
    cancelPendingReveals();
    updateResult(null);
    setCoachMessages([]);
    setCoachStreaming(false);
  };

  const handleSubmit = async () => {
    if (!answer.trim()) return;
    setPhase('streaming');
    setError(null);
    abortRef.current?.abort();
    cancelPendingReveals();
    updateResult(null);
    setCoachMessages([]);
    setCoachStreaming(false);

    abortRef.current = new AbortController();

    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      const service = new AgentService(token);
      let threadId: string | null = null;

      await service.streamEvaluate(
        { message: answer, competencyId, questionType, questionText },
        {
          signal: abortRef.current.signal,
          onEvent: (event) => {
            if (event.type === StreamEventType.Complete) {
              threadId = event.threadId;
            }
          },
          onError: (err) => {
            setError(err.message);
            setPhase('error');
          },
          onComplete: async () => {
            if (!threadId) {
              setError('No thread ID returned from evaluation');
              setPhase('error');
              return;
            }
            setPhase('fetching');
            try {
              const freshToken = await getToken();
              if (!freshToken) throw new Error('Not authenticated');
              const freshService = new AgentService(freshToken);
              const assessment = await freshService.getAssessmentResponse(threadId);
              updateResult(assessment);
              setPhase('done');

              // Reveal the rationale word-by-word into a fresh coach message,
              // independent of `phase` — the score card is already visible.
              setCoachMessages([{ role: ConversationRole.Coach, content: '', isStreaming: true }]);
              setCoachStreaming(true);
              const cancelReveal = streamText(
                assessment.rationale,
                appendCoachChunk,
                finalizeCoachMessage
              );
              registerCanceller(cancelReveal);
            } catch (fetchErr) {
              setError(fetchErr instanceof Error ? fetchErr.message : 'Failed to fetch result');
              setPhase('error');
            }
          },
        }
      );
    } catch (err) {
      if (abortRef.current?.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setPhase('error');
    }
  };

  const handleFollowUp = useCallback(
    (message: string) => {
      if (!message.trim() || coachStreaming) return;

      addUserCoachMessage(message);
      setCoachMessages((prev) => [...prev, { role: ConversationRole.Coach, content: '', isStreaming: true }]);
      setCoachStreaming(true);

      (async () => {
        try {
          const token = await getToken();
          if (!token) {
            showCoachError('Not authenticated');
            return;
          }
          const service = new AgentService(token);

          await service.streamChat(
            {
              message,
              competencyId,
              questionType,
              threadId: result?.threadId ?? undefined,
            },
            {
              signal: abortRef.current?.signal,
              onEvent: (event) => {
                if (event.type === StreamEventType.Chunk) {
                  appendCoachChunk(event.text);
                }
              },
              onError: (err) => showCoachError(err.message),
              onComplete: () => finalizeCoachMessage(),
            }
          );
        } catch (err) {
          if (abortRef.current?.signal.aborted) return;
          showCoachError(err instanceof Error ? err.message : 'Something went wrong');
        }
      })();
    },
    [
      coachStreaming,
      competencyId,
      questionType,
      result,
      getToken,
      addUserCoachMessage,
      appendCoachChunk,
      finalizeCoachMessage,
      showCoachError,
    ]
  );

  const handleCancel = () => {
    abortRef.current?.abort();
    cancelPendingReveals();
    setPhase('idle');
    setCoachMessages([]);
    setCoachStreaming(false);
  };

  const isRunning = phase === 'streaming' || phase === 'fetching';
  const canChat = !coachStreaming && result !== null;
  const strengthScore =
    result?.overallScore != null ? Math.round(result.overallScore) : calcStrength(answer);

  return (
    <motion.div className={styles.page} variants={containerVariants} initial="hidden" animate="visible">
      <motion.div className={styles.header} variants={itemVariants}>
        <Title level={2} className={styles.title}>
          Evaluate Answer
        </Title>
        <Text type="secondary" className={styles.pageSub}>
          Select a competency, pick a question, write an answer, and run a scored evaluation.
        </Text>
      </motion.div>

      <motion.div variants={itemVariants}>
      <Card className={styles.formCard}>
        <div className={styles.selectors}>
          <div className={styles.field}>
            <Text strong>Competency</Text>
            <Select
              options={COMPETENCY_OPTIONS}
              value={competencyId}
              onChange={(v) => { setCompetencyId(v); resetResultAndCoach(); }}
              disabled={isRunning}
              className={styles.select}
            />
          </div>

          <div className={styles.field}>
            <Text strong>Question Type</Text>
            <Select
              options={QUESTION_TYPE_OPTIONS}
              value={questionType}
              onChange={(v) => { setQuestionType(v); resetResultAndCoach(); }}
              disabled={isRunning}
              className={styles.select}
            />
          </div>
        </div>

        <div className={styles.field}>
          <Text strong>Question</Text>
          <Select
            value={selectedQuestionId ?? undefined}
            onChange={(v) => { setSelectedQuestionId(v); resetResultAndCoach(); }}
            options={questions.map((q, i) => ({ value: q.id, label: `${i + 1}. ${q.text}` }))}
            loading={questionsLoading}
            disabled={isRunning || questions.length === 0}
            placeholder={
              questionsLoading
                ? 'Loading questions…'
                : questions.length === 0
                  ? 'No questions available for this competency and type'
                  : 'Select a question'
            }
            className={styles.select}
          />
        </div>

        {questionText && (
          <div className={styles.question}>
            <Text type="secondary" className={styles.questionLabel}>Question</Text>
            <Paragraph className={styles.questionText}>{questionText}</Paragraph>
          </div>
        )}

        <div className={styles.field}>
          <Text strong>Your Answer</Text>
          <TextArea
            value={answer}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAnswer(e.target.value)}
            placeholder="Write your answer here..."
            rows={8}
            disabled={isRunning}
            className={styles.textarea}
          />
        </div>

        <div className={styles.actions}>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={phase === 'fetching'}
            disabled={isRunning || !answer.trim()}
          >
            {phase === 'streaming' ? 'Evaluating...' : 'Evaluate'}
          </Button>
          {isRunning && (
            <Button onClick={handleCancel}>Cancel</Button>
          )}
        </div>
      </Card>

      {error && (
        <Alert type="error" message={error} showIcon className={styles.alert} />
      )}

      {isRunning && (
        <Card className={styles.loadingCard}>
          <Spin />
          <Text type="secondary">
            {phase === 'streaming'
              ? 'Evaluating your answer…'
              : 'Loading assessment result…'}
          </Text>
        </Card>
      )}

      {result && !isRunning && (
        <div className={styles.resultsGrid}>
          <Title level={4} className={styles.resultsTitle}>Assessment Result</Title>
          <div className={styles.results}>
            <div className={styles.summaryPanel}>
              <AssessmentSummary response={result} />
            </div>

            {coachMessages.length > 0 && (
              <div className={styles.coachPanel}>
                <CoachPanel
                  messages={coachMessages}
                  isStreaming={coachStreaming}
                  onSendMessage={handleFollowUp}
                  canChat={canChat}
                  strengthScore={strengthScore}
                />
              </div>
            )}

            <div className={styles.dimensionBreakdown}>
              <DimensionBreakdown
                dimensionScores={result.dimensionScores}
                competencyId={result.competencyId}
                questionType={result.questionType}
                structureScore={result.structureScore}
                structureRationale={result.structureRationale}
                structureComponentScores={result.structureComponentScores}
              />
            </div>
          </div>
        </div>
      )}
      </motion.div>
    </motion.div>
  );
};
